import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, useAuth } from "@/context/AuthContext";
import { PLATFORM_TAGS } from "@/lib/platforms";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Record() {
  const { user, checking, setEntryOpen } = useAuth();
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get("/record").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const remove = async (id) => {
    await api.delete(`/record/${id}`);
    setItems((it) => it.filter((x) => x.id !== id));
    toast("Removed from your record.");
  };

  if (checking) return <main className="pt-16 min-h-[60vh]" />;

  if (!user) {
    return (
      <main className="pt-16" data-testid="record-page-anon">
        <section className="max-w-2xl mx-auto px-5 sm:px-8 pt-20 pb-24 text-center">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl">Your record waits for an alias</h1>
          <p className="mt-4 text-[var(--ink-soft)] max-w-md mx-auto">
            A private, cross platform view of everything you keep, make and answer. Never a dashboard of scores. It needs a name that is not yours.
          </p>
          <button onClick={() => setEntryOpen(true)} className="btn-ink rounded-full px-7 py-3 font-semibold mt-8" data-testid="record-alias-btn">
            Pick your alias
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="pt-16" data-testid="record-page">
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-24">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-full blob-c" style={{ background: user.colour }} />
          <div>
            <h1 className="font-display text-4xl sm:text-5xl">{user.alias}</h1>
            <p className="text-sm text-[var(--ink-soft)] font-mono-nc mt-1">Your record. Only you can see this.</p>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="mt-12 rounded-3xl border border-[var(--line)] bg-white/40 p-8" data-testid="record-empty">
            <h2 className="font-display text-2xl">Nothing kept yet</h2>
            <p className="mt-2 text-[var(--ink-soft)]">Walk through the seven places and keep whatever matters. It all lands here, and nowhere else.</p>
            <Link to="/" className="btn-line inline-block rounded-full px-5 py-2.5 text-sm font-semibold mt-5" data-testid="record-explore-link">
              See the seven
            </Link>
          </div>
        ) : (
          <div className="mt-10 space-y-3" data-testid="record-list">
            {items.map((it) => {
              const tag = PLATFORM_TAGS[it.platform] || PLATFORM_TAGS.general;
              return (
                <div key={it.id} className="rounded-2xl border border-[var(--line)] bg-white/45 px-5 py-4 flex items-center justify-between gap-4" data-testid={`record-item-${it.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: tag.hue }} />
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{it.title}</p>
                      <p className="text-xs text-[var(--ink-soft)] font-mono-nc">{tag.name} · {it.kind}{it.note ? ` · ${it.note}` : ""}</p>
                    </div>
                  </div>
                  <button onClick={() => remove(it.id)} className="text-[var(--ink-soft)] hover:text-[#D64533] transition-colors shrink-0" aria-label="Remove" data-testid={`record-delete-${it.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
